import { Injectable } from '@angular/core';

import { DIAGNOSTIC_REDACTION_PATTERNS } from '../constants/diagnostic-redaction.const';
import { DiagnosticEvent } from '../models/diagnostic-event';
import { DiagnosticEventType } from '../models/diagnostic-profile';

const REDACTED = '***';
const MAX_CLAIM_LEN = 200;

@Injectable({ providedIn: 'root' })
export class BundleSanitizerService {
  /**
   * Returns a sanitized copy of the events. Each variant is rebuilt with
   * a new payload object (shallow spread + per-field override), and any
   * `extra` payload is walked recursively. The input array and its event
   * objects are not mutated; whether parts of the copy alias the original
   * depends on the payload shape (today's payloads are primitives or
   * nested `extra`, which is recursively cloned).
   */
  sanitizeEvents(events: readonly DiagnosticEvent[]): DiagnosticEvent[] {
    return events.map((event) => this.sanitizeEvent(event));
  }

  /**
   * Sanitize a free-text string. Replaces JWT patterns (anchored on the
   * `eyJ` base64url header prefix) with the claim-aware mask form.
   */
  sanitizeString(input: string): string {
    return input.replace(DIAGNOSTIC_REDACTION_PATTERNS.jwtPattern, (match) =>
      this.maskJwt(match)
    );
  }

  /**
   * Redacts known forbidden query parameter values (case-insensitive) in
   * both the URL's `search` and its `hash` portion (the latter for OAuth
   * implicit flow tokens like `#access_token=...`). Returns the input
   * unchanged if no parameters need redacting or if the URL is malformed.
   * Original query-string order and duplicate keys (e.g. `?page=1&page=2`)
   * are preserved for unaffected parameters.
   */
  sanitizeUrl(url: string): string {
    const isRelative = url.startsWith('/') && !url.startsWith('//');
    let parsed: URL;
    try {
      parsed = isRelative ? new URL(url, 'http://placeholder') : new URL(url);
    } catch {
      return url;
    }
    const forbidden = new Set<string>(
      DIAGNOSTIC_REDACTION_PATTERNS.forbiddenQueryParams.map((p) =>
        p.toLowerCase()
      )
    );

    const queryModified = this.redactSearchParams(
      parsed.searchParams,
      forbidden
    );

    let hashModified = false;
    if (parsed.hash.startsWith('#') && parsed.hash.length > 1) {
      const hashParams = new URLSearchParams(parsed.hash.substring(1));
      hashModified = this.redactSearchParams(hashParams, forbidden);
      if (hashModified) {
        parsed.hash = `#${hashParams.toString()}`;
      }
    }

    if (!queryModified && !hashModified) {
      return url;
    }
    if (isRelative) {
      return parsed.pathname + parsed.search + parsed.hash;
    }
    return parsed.toString();
  }

  /**
   * Apply the same redaction rules used on events to an arbitrary
   * structured object (today: the correlation.json content). Returns a
   * deep-cloned copy with every string leaf passed through the URL +
   * JWT chain, and forbidden header keys (if present in nested
   * objects) replaced with '***'.
   */
  sanitizeCorrelation<T>(data: T): T {
    return this.sanitizeUnknown(data) as T;
  }

  /**
   * Mask a JWT to the claim-aware
   *   [JWT iss=... sub=... exp=... hash=<first 8 chars of signature>]
   * format. Falls back to '***JWT***' when the input is not a parseable
   * 3-segment JWT. Claim values are escaped so a malicious or compromised
   * auth server cannot inject characters that break the mask format.
   */
  maskJwt(jwt: string): string {
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      return '***JWT***';
    }
    let claims: Record<string, unknown>;
    try {
      const payloadJson = base64UrlDecode(parts[1]);
      claims = JSON.parse(payloadJson) as Record<string, unknown>;
    } catch {
      return '***JWT***';
    }
    const iss = safeClaim(claims['iss']);
    const sub = safeClaim(claims['sub']);
    const exp = safeClaim(claims['exp']);
    const hash = parts[2].slice(0, 8);
    return `[JWT iss=${iss} sub=${sub} exp=${exp} hash=${hash}]`;
  }

  private sanitizeEvent(event: DiagnosticEvent): DiagnosticEvent {
    switch (event.type) {
      case DiagnosticEventType.ROUTE:
        return {
          ...event,
          payload: {
            ...event.payload,
            url: this.sanitizeString(this.sanitizeUrl(event.payload.url)),
          },
        };
      case DiagnosticEventType.API: {
        const payload = event.payload;
        const sanitized: typeof payload = {
          ...payload,
          url: this.sanitizeString(this.sanitizeUrl(payload.url)),
        };
        // EAS-2043: body excerpts go through the same URL+JWT chain we
        // apply to other free-text leaves. Headers go through
        // sanitizeUnknown so the existing forbidden-headers list
        // (Authorization, Cookie, …) replaces sensitive values with
        // '***' regardless of casing.
        if (payload.requestBody !== undefined) {
          sanitized.requestBody = this.sanitizeBody(payload.requestBody);
        }
        if (payload.responseBody !== undefined) {
          sanitized.responseBody = this.sanitizeBody(payload.responseBody);
        }
        if (payload.requestHeaders !== undefined) {
          sanitized.requestHeaders = this.sanitizeUnknown(
            payload.requestHeaders
          ) as Record<string, string>;
        }
        if (payload.responseHeaders !== undefined) {
          sanitized.responseHeaders = this.sanitizeUnknown(
            payload.responseHeaders
          ) as Record<string, string>;
        }
        return { ...event, payload: sanitized };
      }
      case DiagnosticEventType.ERROR:
        return {
          ...event,
          payload: {
            ...event.payload,
            message: this.sanitizeString(event.payload.message),
            source: event.payload.source
              ? this.sanitizeString(event.payload.source)
              : event.payload.source,
            stack: event.payload.stack
              ? this.sanitizeString(event.payload.stack)
              : event.payload.stack,
          },
        };
      case DiagnosticEventType.USER_ACTION:
        return {
          ...event,
          payload: {
            ...event.payload,
            kind: this.sanitizeString(this.sanitizeUrl(event.payload.kind)),
            target: event.payload.target
              ? this.sanitizeString(this.sanitizeUrl(event.payload.target))
              : event.payload.target,
            extra: event.payload.extra
              ? (this.sanitizeUnknown(event.payload.extra) as Record<
                  string,
                  unknown
                >)
              : event.payload.extra,
          },
        };
    }
  }

  /**
   * JSON-aware body redaction. The body excerpt from the interceptor
   * is a serialized JSON string in the common case; redacting at the
   * string level alone (JWT-mask only) leaves `password`, `cardNumber`,
   * and other credential / PII values plain-text in the bundle. This
   * method:
   *   1. Runs the string-level chain (URL + JWT) as a fallback for
   *      non-JSON bodies (form-urlencoded, plain text, partials).
   *   2. Tries to parse the result as JSON and run `sanitizeUnknown`
   *      on the structured form — keys in `forbiddenBodyKeys` /
   *      `forbiddenHeaders` are redacted to '***' regardless of how
   *      deeply they nest.
   *   3. Re-serializes if parsing succeeded.
   *
   * This is the only sanitization layer between a frontend-typed
   * `password` value and the support engineer's mailbox — there is
   * no role gate on `requestBodyMaxChars` for that field (see
   * EAS-2043 review #2). Adding a key to `forbiddenBodyKeys` /
   * bumping `BUNDLE_POLICY_VERSION` is the supported way to extend.
   */
  private sanitizeBody(rawBody: string): string {
    const stringSanitized = this.sanitizeString(this.sanitizeUrl(rawBody));
    try {
      const parsed = JSON.parse(stringSanitized);
      const redacted = this.sanitizeUnknown(parsed);
      return JSON.stringify(redacted);
    } catch {
      // Not JSON — the string-level pass is the best we can do.
      return stringSanitized;
    }
  }

  /**
   * Walk an unknown value and apply both URL-aware and JWT-aware
   * sanitization to every string leaf. Object keys matching the forbidden
   * headers OR the forbidden body keys list have their value replaced
   * with '***'. Primitive non-string types pass through unchanged.
   */
  private sanitizeUnknown(value: unknown): unknown {
    if (typeof value === 'string') {
      // Compose URL redaction with JWT regex. sanitizeUrl is a no-op on
      // non-URL strings (returns the input on parse failure), so the
      // composition is safe for arbitrary leaves.
      return this.sanitizeString(this.sanitizeUrl(value));
    }
    if (Array.isArray(value)) {
      return value.map((v) => this.sanitizeUnknown(v));
    }
    if (value !== null && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      // Normalize both the list and each incoming key (strip _ and -)
      // so that `new_password`, `new-password`, `newPassword`, and
      // `NEWPASSWORD` all match a single `newpassword` entry. New
      // backend payload styles thus don't require touching the list.
      const forbiddenKeys = new Set<string>(
        [
          ...DIAGNOSTIC_REDACTION_PATTERNS.forbiddenHeaders,
          ...DIAGNOSTIC_REDACTION_PATTERNS.forbiddenBodyKeys,
        ].map(normalizeKey)
      );
      for (const [k, v] of Object.entries(value)) {
        if (forbiddenKeys.has(normalizeKey(k))) {
          result[k] = REDACTED;
        } else {
          result[k] = this.sanitizeUnknown(v);
        }
      }
      return result;
    }
    return value;
  }

  /**
   * Redact every value whose key (case-insensitive) is in the forbidden
   * set. Modifies the given `URLSearchParams` in place. Preserves the
   * count of duplicate keys (e.g. `?page=1&page=2` is preserved; only the
   * forbidden keys are replaced).
   *
   * Returns true if at least one parameter was redacted.
   */
  private redactSearchParams(
    params: URLSearchParams,
    forbidden: Set<string>
  ): boolean {
    const keysToRedact = new Set<string>();
    params.forEach((_value, key) => {
      if (forbidden.has(key.toLowerCase())) {
        keysToRedact.add(key);
      }
    });
    if (keysToRedact.size === 0) {
      return false;
    }
    keysToRedact.forEach((key) => {
      const count = params.getAll(key).length;
      params.delete(key);
      for (let i = 0; i < count; i++) {
        params.append(key, REDACTED);
      }
    });
    return true;
  }
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[_-]/g, '');
}

function base64UrlDecode(input: string): string {
  let base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = base64.length % 4;
  if (padding > 0) {
    base64 += '='.repeat(4 - padding);
  }
  return atob(base64);
}

/**
 * Escape characters that would break the `[JWT k=v k=v ...]` mask format:
 * - `]` closes the mask early
 * - whitespace would create a fake new `key=value` pair
 * - `=` would inject extra key=value separators (`iss=foo=bar` reads as
 *   two assignments by a naive parser)
 * - control characters confuse most consumers
 *
 * Also clamps the length so a pathological `iss` URL cannot inflate the mask.
 */
function safeClaim(value: unknown): string {
  const str = String(value ?? '?');
  // eslint-disable-next-line no-control-regex
  return str.replace(/[\]\s=\x00-\x1f\x7f]/g, '_').slice(0, MAX_CLAIM_LEN);
}
